import React, { useState, useEffect, useContext } from 'react';
import { Dropdown, Button, Spinner, Table, Card, Row, Col, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TodoApp from '../../components/apps/TodoApp';
import axiosInstance from '../../api/axios';
import { AuthContext } from '../../context/AuthContext';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Dashboard() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { isDark } = useContext(ThemeContext) || {};
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stats States
  const [stats, setStats] = useState({
    pendingWeighing: 0,
    pendingPayment: 0,
    overdueInvoices: 0,
    totalSales: 0,
    readyOrDispatched: 0,
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/sales/orders');
      if (response.data.success && response.data.data) {
        const ordersList = Array.isArray(response.data.data) 
          ? response.data.data 
          : (response.data.data.data || []);
        setOrders(ordersList);
        calculateStats(ordersList);
      }
    } catch (error) {
      console.error('Error fetching dashboard orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersList) => {
    let pendingWeighing = 0;
    let pendingPayment = 0;
    let overdueInvoices = 0;
    let totalSales = 0;
    let readyOrDispatched = 0;

    ordersList.forEach(order => {
      const invoiceStatus = order.estatus_factura || order.estatus;
      const isPendingCobranza = ['Pendiente por Cobrar', 'Por Pagar', 'Rechazado'].includes(invoiceStatus);

      if (order.estatus === 'Pendiente de Pesaje' || order.estatus_pedido === 'Pendiente de Pesaje') pendingWeighing++;
      if (isPendingCobranza) {
        pendingPayment++;
        if (order.is_overdue) {
          overdueInvoices++;
        }
      }
      if (['Listo para Despacho', 'Despachado', 'Entregado'].includes(order.estatus) || ['En Despacho', 'Entregado', 'Completado'].includes(order.estatus_pedido)) {
        readyOrDispatched++;
      }

      if (order.estatus !== 'Cancelado' && order.estatus_pedido !== 'Cancelado') {
        totalSales += parseFloat(order.total_base_usd || 0);
      }
    });

    setStats({
      pendingWeighing,
      pendingPayment,
      overdueInvoices,
      totalSales,
      readyOrDispatched
    });
  };

  // Chart: Sales by day of week
  const getSalesDataByDay = () => {
    const days = [
      t('day_sunday', 'Domingo'),
      t('day_monday', 'Lunes'),
      t('day_tuesday', 'Martes'),
      t('day_wednesday', 'Miércoles'),
      t('day_thursday', 'Jueves'),
      t('day_friday', 'Viernes'),
      t('day_saturday', 'Sábado')
    ];
    const sales = [0, 0, 0, 0, 0, 0, 0];
    
    orders.forEach(order => {
      if (order.estatus !== 'Cancelado' && order.created_at) {
        const date = new Date(order.created_at);
        const dayIndex = date.getDay();
        sales[dayIndex] += parseFloat(order.total_base_usd || 0);
      }
    });

    return {
      labels: days,
      datasets: [
        {
          label: t('sales_by_day_dataset', 'Ventas por Día ($)'),
          data: sales,
          backgroundColor: 'rgba(24, 62, 235, 0.7)',
          borderColor: '#183eeb',
          borderWidth: 2,
          borderRadius: 6,
        }
      ]
    };
  };

  const salesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      y: {
        grid: { drawBorder: false, color: isDark ? '#233052' : '#f2f2f2' },
        ticks: { color: isDark ? '#94a3b8' : '#8c909a', font: { size: 11 } }
      },
      x: {
        grid: { display: false },
        ticks: { color: isDark ? '#94a3b8' : '#8c909a', font: { size: 11 } }
      }
    }
  };

  // Helper to get status badge class
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pendiente de Pesaje':
        return 'badge bg-warning text-dark';
      case 'Por Pagar':
        return 'badge bg-danger text-white';
      case 'Listo para Despacho':
        return 'badge bg-info text-white';
      case 'Despachado':
      case 'Entregado':
        return 'badge bg-success text-white';
      default:
        return 'badge bg-secondary text-white';
    }
  };

  const recentOrders = orders.slice(0, 6);

  return (
    <>
      {/* Page Header */}
      <div className="page-header flex-wrap">
        <h3 className="mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold' }}>
          {t('dashboard_title', 'Consola del Operador')}
          <span className="ps-0 h6 ps-sm-2 text-muted d-inline-block font-weight-normal" style={{ fontSize: '14px' }}>
            {t('dashboard_subtitle', 'Resumen en tiempo real de ventas, pesaje y rutas.')}
          </span>
        </h3>
        <div className="d-flex">
          <Button 
            onClick={fetchOrders} 
            className="btn-sm btn-light btn-icon-text border me-2"
            disabled={loading}
          >
            <i className="mdi mdi-refresh btn-icon-prepend"></i> {t('reload_data', 'Recargar Datos')}
          </Button>
          <Link to="/ecommerce/orders" className="btn-sm btn-icon-text text-white border-0" style={{ backgroundColor: '#183eeb', borderRadius: '6px', fontWeight: 'bold', padding: '8px 16px' }}>
            <i className="mdi mdi-cart btn-icon-prepend"></i> {t('menu_orders_console', 'Consola de Pedidos')}
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="my-4">
          <Row className="mb-4">
            {[1, 2, 3, 4].map((idx) => (
              <Col xl={3} md={6} key={idx} className="mb-3 mb-xl-0">
                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', backgroundColor: isDark ? '#161e38' : '#f8fafc', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0', minHeight: '110px' }}>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <LoadingState size="sm" py="py-2" minHeight="60px" />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="border-0 shadow-sm" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
            <Card.Body className="p-4">
              <LoadingState
                size="md"
                title={t('loading_dashboard', 'Cargando métricas y estadísticas...')}
                description={t('loading_dashboard_sub', 'Sincronizando estados de pesaje, pedidos por cobrar y despachos activos en tiempo real')}
                minHeight="260px"
              />
            </Card.Body>
          </Card>
        </div>
      ) : (
        <>
          {/* Row 1: Stats Cards */}
          <Row className="mb-4">
            {/* Pending Weighing */}
            <Col xl={3} md={6} className="mb-3 mb-xl-0">
              <Card className="bg-warning border-0 shadow-sm" style={{ borderRadius: '12px', color: '#fff' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 text-white-50 text-uppercase small fw-bold">{t('pending_weighing', 'Por Pescar/Pesar')}</p>
                      <h2 className="mb-0 fw-bold">{stats.pendingWeighing} <span className="h6 text-white-50">{t('orders_count_label', 'pedidos')}</span></h2>
                    </div>
                    <div className="bg-inverse-icon-warning p-2 rounded-circle">
                      <i className="mdi mdi-scale-balance text-white h4 mb-0"></i>
                    </div>
                  </div>
                  <div className="mt-3 small text-white-50">{t('pending_weighing_desc', 'Pedidos en espera de báscula en depósito')}</div>
                </Card.Body>
              </Card>
            </Col>

            {/* Pending Payment */}
            <Col xl={3} md={6} className="mb-3 mb-xl-0">
              <Card className="bg-danger border-0 shadow-sm" style={{ borderRadius: '12px', color: '#fff' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 text-white-50 text-uppercase small fw-bold">{t('pending_payment', 'Por Cobrar')}</p>
                      <h2 className="mb-0 fw-bold">{stats.pendingPayment} <span className="h6 text-white-50">{t('orders_count_label', 'pedidos')}</span></h2>
                    </div>
                    <div className="bg-inverse-icon-danger p-2 rounded-circle">
                      <i className="mdi mdi-currency-usd text-white h4 mb-0"></i>
                    </div>
                  </div>
                  <div className="mt-2 d-flex align-items-center justify-content-between text-white-50 small">
                    <span>{t('pending_payment_desc', 'Facturas pendientes')}</span>
                    {stats.overdueInvoices > 0 && (
                      <span className="badge bg-white text-danger fw-bold">
                        {stats.overdueInvoices} {t('overdue_count_label', 'vencida(s)')}
                      </span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Sales */}
            <Col xl={3} md={6} className="mb-3 mb-xl-0">
              <Card className="bg-primary border-0 shadow-sm" style={{ borderRadius: '12px', color: '#fff' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 text-white-50 text-uppercase small fw-bold">{t('kpi_total_sales', 'Ventas Totales')}</p>
                      <h2 className="mb-0 fw-bold">${stats.totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
                    </div>
                    <div className="bg-inverse-icon-primary p-2 rounded-circle">
                      <i className="mdi mdi-cash-multiple text-white h4 mb-0"></i>
                    </div>
                  </div>
                  <div className="mt-3 small text-white-50">{t('total_sales_desc', 'Monto total facturado (excluye cancelados)')}</div>
                </Card.Body>
              </Card>
            </Col>

            {/* Dispatch Ready */}
            <Col xl={3} md={6} className="mb-3 mb-xl-0">
              <Card className="bg-success border-0 shadow-sm" style={{ borderRadius: '12px', color: '#fff' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 text-white-50 text-uppercase small fw-bold">{t('ready_dispatched', 'Listos / Despachados')}</p>
                      <h2 className="mb-0 fw-bold">{stats.readyOrDispatched} <span className="h6 text-white-50">{t('orders_count_label', 'pedidos')}</span></h2>
                    </div>
                    <div className="bg-inverse-icon-success p-2 rounded-circle">
                      <i className="mdi mdi-truck-delivery text-white h4 mb-0"></i>
                    </div>
                  </div>
                  <div className="mt-3 small text-white-50">{t('ready_dispatched_desc', 'Pedidos consolidados listos para la ruta')}</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Row 2: Charts and Status distribution */}
          <Row className="mb-4">
            <Col xl={8} className="mb-3 mb-xl-0">
              <Card className="shadow-sm border-0" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028' }}>{t('sales_by_day_title', 'Ventas por Día de la Semana')}</h5>
                    <span className="badge bg-light text-primary border">{t('updated_today', 'Actualizado hoy')}</span>
                  </div>
                  <div style={{ height: '280px' }}>
                    <Bar data={getSalesDataByDay()} options={salesChartOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="shadow-sm border-0" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-4" style={{ color: isDark ? '#ffffff' : '#010028' }}>{t('system_quick_shortcuts', 'Accesos Operativos')}</h5>
                  
                  <div className="d-grid gap-3">
                    <Link to="/ecommerce/orders" className="btn btn-outline-warning text-start d-flex align-items-center justify-content-between p-3" style={{ borderRadius: '10px' }}>
                      <div className="d-flex align-items-center">
                        <i className="mdi mdi-scale text-warning fs-4 me-3"></i>
                        <div>
                          <div className="fw-bold text-dark">{t('shortcut_weighing', 'Módulo de Pesaje')}</div>
                          <small className="text-muted">{stats.pendingWeighing} {t('shortcut_weighing_sub', 'pedidos en cola')}</small>
                        </div>
                      </div>
                      <i className="mdi mdi-chevron-right text-muted"></i>
                    </Link>

                    <Link to="/ecommerce/orders" className="btn btn-outline-info text-start d-flex align-items-center justify-content-between p-3" style={{ borderRadius: '10px' }}>
                      <div className="d-flex align-items-center">
                        <i className="mdi mdi-truck-fast text-info fs-4 me-3"></i>
                        <div>
                          <div className="fw-bold text-dark">{t('shortcut_dispatch', 'Módulo de Despachos')}</div>
                          <small className="text-muted">{stats.readyOrDispatched} {t('shortcut_dispatch_sub', 'pedidos listos')}</small>
                        </div>
                      </div>
                      <i className="mdi mdi-chevron-right text-muted"></i>
                    </Link>

                    <Link to="/ecommerce/catalogue" className="btn btn-outline-primary text-start d-flex align-items-center justify-content-between p-3" style={{ borderRadius: '10px' }}>
                      <div className="d-flex align-items-center">
                        <i className="mdi mdi-package-variant text-primary fs-4 me-3"></i>
                        <div>
                          <div className="fw-bold text-dark">{t('shortcut_catalogue', 'Catálogo de Productos')}</div>
                          <small className="text-muted">{t('shortcut_catalogue_sub', 'Precios, stock y fotos')}</small>
                        </div>
                      </div>
                      <i className="mdi mdi-chevron-right text-muted"></i>
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Row 3: Recent Orders Table & Todo */}
          <Row>
            <Col xl={8} className="mb-3 mb-xl-0">
              <Card className="shadow-sm border-0" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#010028' }}>{t('recent_orders_title', 'Últimos Pedidos en Proceso')}</h5>
                    <Link to="/ecommerce/orders" className="small text-primary fw-bold text-decoration-none">{t('view_all_orders', 'Ver todos →')}</Link>
                  </div>

                  <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                    <Table responsive hover className="align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0">{t('th_order_number', 'Nro Pedido')}</th>
                          <th className="border-0">{t('th_client', 'Cliente')}</th>
                          <th className="border-0">{t('th_total', 'Total')}</th>
                          <th className="border-0 text-center">{t('th_status', 'Estado')}</th>
                          <th className="border-0 text-end">{t('th_action', 'Acción')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentOrders.length > 0 ? recentOrders.map(order => (
                          <tr key={order.id}>
                            <td className="fw-bold text-primary">{order.nro_pedido}</td>
                            <td>
                              <div className="fw-semibold">{order.cliente?.nombre_comercial || order.cliente?.razon_social || t('client_generic', 'Cliente')}</div>
                              <small className="text-muted">{order.cliente?.rif}</small>
                            </td>
                            <td className="fw-bold">${parseFloat(order.total_base_usd || 0).toFixed(2)}</td>
                            <td className="text-center">
                              <span className={getStatusBadge(order.estatus)}>
                                {order.estatus}
                              </span>
                            </td>
                            <td className="text-end">
                              <Link to="/ecommerce/orders" className="btn btn-sm btn-light text-primary border" style={{ borderRadius: '6px' }}>
                                <i className="mdi mdi-eye"></i>
                              </Link>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">
                              {t('no_recent_orders', 'No hay pedidos recientes en el sistema.')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Todo App Integration */}
            <Col xl={4}>
              <Card className="shadow-sm border-0" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3" style={{ color: isDark ? '#ffffff' : '#010028' }}>{t('activity_log', 'Registro de Actividad')}</h5>
                  <ul className="gradient-bullet-list border-bottom mb-0">
                    <li className="pb-3 pt-2">
                      <h6 className="mb-0 fw-bold">{t('log_scale_calibrated', 'Báscula de Depósito calibrada exitosamente.')}</h6>
                      <p className="text-muted small">2h</p>
                    </li>
                    <li className="pb-3 pt-2">
                      <h6 className="mb-0 fw-bold">{t('log_route_sheet', 'Se generó la Hoja de Carga para la Ruta Norte.')}</h6>
                      <p className="text-muted small">4h</p>
                    </li>
                    <li className="pb-3 pt-2">
                      <h6 className="mb-0 fw-bold">{t('log_inventory_update', 'Actualización masiva de inventario completada.')}</h6>
                      <p className="text-muted small">1d</p>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

export default Dashboard;
